import React, { FC } from 'react';
import {
  ClockIcon,
  UserAltIcon,
  LocationAltIcon,
  InfoIcon,
  RatingFilledIcon
} from 'cokitchen-icons';

import { ReactComponent as BikeIcon } from 'assets/icons/bike-icon.svg';
import { Link } from 'react-router-dom';
import Tippy from '@tippyjs/react';

import {
  formatNumber,
  getNameFromOrder,
  getOrderStatus,
  getUserName,
  numberWithCommas
} from 'utils/functions';
import OrderStatus from 'components/base/order-status';
import ArrivalTimeTag from 'components/base/arrival-time-tag';
import { CsRepTripHistoryType } from 'models/trip-history.model';

type Props = {
  trip: CsRepTripHistoryType;
};

const RepTripHistoryCard: FC<Props> = ({ trip }) => {
  const createdDateObj = new Date(trip?.created_at ?? '');
  const createdTime = createdDateObj.toLocaleString('en-NG', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  const completedDateObj = new Date(trip?.order.kitchen_dispatched_time ?? '');
  const completedTime = completedDateObj.toLocaleString('en-NG', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  const delivery_fee = numberWithCommas(
    (
      Number(trip.order.calculated_order?.delivery_fee) +
      Number(trip.order.calculated_order?.delivery_service_charge)
    ).toFixed(2)
  );

  const customer_name = getNameFromOrder(trip.order);
  const rider_name = getUserName(trip.order.rider);

  return (
    <div className='border border-gray-border rounded-lg flex flex-col px-3'>
      <div className='grow py-3 space-y-2'>
        <div className='flex border-b border-[#ECEDEF] pb-2 items-center gap-2 flex-wrap justify-between'>
          <div className='flex gap-2 items-center'>
            <h4 className='font-medium text-base cursor-pointer'>
              #{trip.order.order_code?.toUpperCase()}
            </h4>
            <OrderStatus type={getOrderStatus(trip.order)} />
          </div>
          <Link
            className='hover:underline font-medium text-xs text-red-5'
            to={`/order/${trip.order.order_code}`}>
            View details
          </Link>
        </div>
        <div className='space-y-3 py-3'>
          <div className='flex justify-between'>
            <div className='text-sm text-gray-8 flex items-center gap-2'>
              <UserAltIcon className='text-gray-4 w-4' />
              {customer_name}
            </div>
            <div className='flex gap-2 items-center'>
              <div className='flex justify-between'>
                <div className='text-sm text-gray-8 flex items-center gap-2'>
                  <BikeIcon className='text-gray-4 w-4' />
                  {rider_name}
                </div>
              </div>
              <div className='flex items-center gap-1 px-[5px] p-1 rounded-md bg-gray-11 text-gray-5 text-xs'>
                3.5 <RatingFilledIcon className='text-yellow-5' />
              </div>
            </div>
          </div>
          <div className='flex justify-between gap-4'>
            <div className='flex justify-between'>
              <div className='text-sm text-gray-8 flex items-center gap-2'>
                <LocationAltIcon className='text-gray-4 w-4 min-w-[16px]' />
                <span>{trip.order.calculated_order?.address_details?.address_line}</span>
              </div>
            </div>
            <div className='text-xs text-[#313030] flex items-center gap-1'>
              <span>
                {formatNumber(delivery_fee, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                })}
              </span>
            </div>
          </div>

          <div className='text-sm text-gray-8 flex items-center gap-2'>
            <span>Time of arrival</span>
            <ArrivalTimeTag text='10 mins' color='green' />
          </div>
          {trip.order.order_details?.rider_note && (
            <div className='border border-[#FDE9AF] bg-[#FEF7E1] text-[#85806F] px-4 py-2 rounded-md text-xs'>
              <p className='flex gap-1 items-start'>
                <InfoIcon /> <span>{trip.order.order_details?.rider_note}</span>
              </p>
            </div>
          )}
        </div>
        <div className='border-t border-gray-border py-2  flex justify-between items-center'>
          <p className='flex text-gray-5 font-medium uppercase min-w-fit items-center gap-2 text-xs hover:underline tracking-wider'>
            <ClockIcon className='w-4 h-4 text-gray-5' />
            {createdTime}
          </p>
          {trip.order.kitchen_dispatched_time && (
            <Tippy
              content={
                <div className=' p-2 px-3 rounded-md shadow-md bg-white text-gray-8 font-medium text-xs'>
                  Dispatch time
                </div>
              }>
              <div className='flex items-center gap-2 p-1 px-3 rounded-[2px] bg-gray-11 border border-[#EFEEEC] text-gray-5 text-xs'>
                <InfoIcon />
                {completedTime}
              </div>
            </Tippy>
          )}
        </div>
      </div>
    </div>
  );
};

export default RepTripHistoryCard;
